import { isValid, format as fnsFormat } from "date-fns";
import { parseServerDate } from "./serverDate";

/**
 * Format a date from formatted string, unix timestamp, or other string to a human-readable format
 * @param {string|number|Date} dateValue - The date value to format
 * @param {string} formatPattern - Format pattern (default: "local" for localized display)
 * @returns {string} - Formatted date string
 */
export const formatDate = (dateValue, formatPattern = "local") => {
  if (!dateValue) return "";

  try {
    let dateObj;

    // Handle string dates with 2025 year
    if (typeof dateValue === "string" && dateValue.includes("2025-")) {
      // Extract the date parts from the 2025 date string
      const parts = dateValue.split(/[- :]/);
      if (parts.length >= 6) {
        // Create a date with current year but keep time components
        const currentYear = new Date().getFullYear();
        // Convert to local timezone by explicitly creating a date with local components
        dateObj = new Date(
          currentYear, // Current year instead of 2025
          parseInt(parts[1]) - 1, // Month (0-based)
          parseInt(parts[2]), // Day
          parseInt(parts[3]), // Hour
          parseInt(parts[4]), // Minute
          parseInt(parts[5]) // Second
        );
      } else {
        // Fallback if format is unexpected
        const currentYear = new Date().getFullYear();
        const correctedDate = dateValue.replace("2025-", `${currentYear}-`);
        dateObj = new Date(correctedDate);
      }
    }
    // Handle numeric timestamps (either as strings or numbers)
    else if (
      typeof dateValue === "number" ||
      (typeof dateValue === "string" && /^\d+$/.test(dateValue))
    ) {
      dateObj = timestampToDate(dateValue);
    }
    // Handle Date objects or other date strings
    else {
      /* Naive DB DATETIMEs are the SERVER's wall clock, not the viewer's. parseServerDate
         recovers the real instant and leaves anything already carrying a zone untouched —
         see serverDate.js for the 3h30m this was costing from New Delhi. */
      dateObj =
        typeof dateValue === "string"
          ? parseServerDate(dateValue) || new Date(dateValue)
          : dateValue;
    }

    if (!dateObj || isNaN(dateObj.getTime())) {
      console.warn(`Invalid date for formatting: ${dateValue}`);
      return "";
    }

    // Fix any remaining 2025 year instances
    if (dateObj.getFullYear() === 2025) {
      dateObj.setFullYear(new Date().getFullYear());
    }

    // Use our format function which handles different patterns
    return format(dateObj, formatPattern);
  } catch (error) {
    console.error(`Error in formatDate: ${dateValue}`, error);
    return "";
  }
};

/**
 * Format a Unix timestamp (milliseconds) with a specific pattern
 * @param {string|number} unixTimestamp - Unix timestamp in milliseconds
 * @param {string} pattern - Format pattern (e.g., "MMM d", "yyyy-MM-dd")
 * @returns {string} - Formatted date string or empty string if invalid
 */
export const formatUnixTimestamp = (unixTimestamp, pattern = "MMM d") => {
  if (!unixTimestamp) return "";

  const date = timestampToDate(unixTimestamp);
  if (!date) return "";

  return format(date, pattern);
};

/**
 * Check if a timestamp is from the future based on current system time
 * @param {Date} date - The date to check
 * @returns {boolean} - True if the date appears to be more than 50 years in the future
 */
const isDateFromDistantFuture = (date) => {
  const currentYear = new Date().getFullYear();
  return date.getFullYear() > currentYear + 50;
};

/**
 * Creates a Date object from a timestamp or date string.
 * Handles timestamps, ISO strings, and custom date formats including 2025 year issues.
 *
 * @param {number|string} timestamp - Unix timestamp (in seconds or milliseconds) or date string
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const timestampToDate = (timestamp) => {
  if (!timestamp) return null;

  try {
    // Handle string date formats like "2025-05-09 18:13:05"
    if (typeof timestamp === "string" && timestamp.includes("-")) {
      // This is a date string, not a numeric timestamp
      return parseDateString(timestamp);
    }

    // Try to parse as number first (for actual timestamps)
    let ts = Number(timestamp);

    // If this is NaN, it's not a numeric timestamp
    if (isNaN(ts)) {
      console.warn(`Invalid timestamp format: ${timestamp}`);
      return null;
    }

    // Check if timestamp appears to be in seconds (typical DB timestamp)
    // This works for timestamps after Sep 9, 2001
    if (ts < 1000000000000) {
      ts *= 1000;
    }

    // Create a date from the adjusted timestamp (already in local timezone)
    const date = new Date(ts);

    // Handle dates with year 2025 (likely wrong future dates)
    if (date.getFullYear() === 2025) {
      console.log("Fixing 2025 date:", date.toISOString());
      date.setFullYear(new Date().getFullYear());

      // If the fixed date is still in the future, adjust it to the previous year
      if (date > new Date()) {
        date.setFullYear(new Date().getFullYear() - 1);
      }
    }

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid timestamp: ${timestamp}`);
      return null;
    }

    return date;
  } catch (error) {
    console.error(`Error converting timestamp: ${timestamp}`, error);
    return null;
  }
};

/**
 * Parse a date string that may have timezone issues or 2025 year problems
 * @param {string} dateStr - Date string to parse
 * @returns {Date|null} - JavaScript Date object or null if invalid
 */
export const parseDateString = (dateStr) => {
  if (!dateStr) return null;

  try {
    // Handle ISO format date strings with Z (already in UTC)
    if (
      typeof dateStr === "string" &&
      dateStr.includes("T") &&
      dateStr.includes("Z")
    ) {
      const date = new Date(dateStr);

      // Fix 2025 year issue if present
      if (date.getFullYear() === 2025) {
        date.setFullYear(new Date().getFullYear());
      }

      return date;
    }

    // Handle the 2025 year issue with proper timezone handling for format like "2025-05-09 18:13:05"
    if (typeof dateStr === "string" && dateStr.includes("2025-")) {
      // Extract date parts from the string
      const parts = dateStr.split(/[- :]/);
      if (parts.length >= 6) {
        // Get the current year
        const currentYear = new Date().getFullYear();

        // For dates in format "2025-05-09 18:13:05", they're in UTC+8
        // Create a proper date in JavaScript

        // The simplest way to handle the timezone correctly:
        // Create the date string in ISO format with the UTC+8 timezone indicator
        const isoString = `${currentYear}-${parts[1]}-${parts[2]}T${parts[3]}:${parts[4]}:${parts[5]}+08:00`;
        return new Date(isoString);
      } else {
        // Fallback for simpler format
        const currentYear = new Date().getFullYear();
        const fixedDateStr = dateStr.replace("2025-", `${currentYear}-`);
        // Assume UTC+8 time zone
        return new Date(`${fixedDateStr} UTC+8`);
      }
    }

    // Parse regular date string
    const date = new Date(dateStr);

    // Check if date is valid
    if (isNaN(date.getTime())) {
      console.warn(`Invalid date string: ${dateStr}`);
      return null;
    }

    return date;
  } catch (error) {
    console.error(`Error parsing date string: ${dateStr}`, error);
    return null;
  }
};

/**
 * Utility debug function to test date conversion
 * @param {string|number} dateValue - Example date value from database
 * @param {string} originalDateStr - Original date string for comparison
 */
export const debugDateConversion = (dateValue, originalDateStr) => {
  console.group("Date Conversion Debug");

  try {
    console.log("Original input:", dateValue);
    if (originalDateStr) {
      console.log("Expected DB date:", originalDateStr);
    }

    let dateObj;

    // Test different parsing methods
    if (
      typeof dateValue === "number" ||
      (typeof dateValue === "string" && /^\d+$/.test(dateValue))
    ) {
      console.log("Treating as timestamp");
      dateObj = timestampToDate(dateValue);
    } else if (typeof dateValue === "string" && dateValue.includes("2025-")) {
      console.log("Treating as 2025-year string (UTC+8 format)");
      dateObj = parseDateString(dateValue);
      console.log("Parsed with timezone correction:", dateObj);
    } else {
      console.log("Treating as regular date string/object");
      dateObj = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    }

    console.log("Parsed to Date object:", dateObj);
    console.log("ISO string:", dateObj?.toISOString());
    console.log("Time value:", dateObj?.getTime());
    console.log("Local string:", dateObj?.toLocaleString());
    console.log("UTC string:", dateObj?.toUTCString());

    // Show timezone offset
    console.log(
      "Local timezone offset:",
      -dateObj?.getTimezoneOffset() / 60,
      "hours"
    );

    // Format using our format function
    console.log("Using format function:");
    console.log("- MMM d:", format(dateObj, "MMM d"));
    console.log("- yyyy-MM-dd:", format(dateObj, "yyyy-MM-dd"));
    console.log("- dd/MM/yyyy:", format(dateObj, "dd/MM/yyyy"));
    console.log("- h:mm a:", format(dateObj, "h:mm a"));
    console.log("- full:", format(dateObj, "full"));

    // Check timestamp conversion
    const timestamp = Math.floor(dateObj.getTime() / 1000);
    console.log("Converted to timestamp:", timestamp);
    const backToDate = timestampToDate(timestamp);
    console.log("Timestamp back to date:", backToDate);
  } catch (error) {
    console.error("Error in debug function:", error);
  }

  console.groupEnd();
};

/**
 * Parse JSON string into object
 * @param {string} jsonString - JSON string to parse
 * @returns {object} - Parsed object or empty object on error
 */
export const parseJsonData = (jsonString) => {
  if (!jsonString) return {};

  try {
    return typeof jsonString === "string" ? JSON.parse(jsonString) : jsonString;
  } catch (e) {
    console.error("Error parsing JSON:", e);
    return {};
  }
};

/**
 * Formats a date according to the specified pattern.
 * Handles various date formats and fixes 2025 year issues.
 *
 * @param {Date|string|number} dateValue - The date to format
 * @param {string} pattern - The pattern to format the date with
 * @returns {string} - The formatted date string or empty string if invalid
 */
export const format = (dateValue, pattern = "MMM d") => {
  if (!dateValue) return "";

  try {
    let date;

    // Handle string dates that start with 2025 year
    if (typeof dateValue === "string" && dateValue.includes("2025-")) {
      const correctedDateStr = dateValue.replace(
        "2025-",
        `${new Date().getFullYear()}-`
      );
      date = new Date(correctedDateStr);
    }
    // Handle numeric timestamps
    else if (
      typeof dateValue === "number" ||
      (typeof dateValue === "string" && /^\d+$/.test(dateValue))
    ) {
      date = timestampToDate(dateValue);
    }
    // Handle Date objects or other date strings
    else {
      date = typeof dateValue === "string" ? new Date(dateValue) : dateValue;
    }

    // Validate the date
    if (!date || !isValid(date)) {
      console.warn(`Invalid date: ${dateValue}`);
      return "";
    }

    // Fix any remaining 2025 year instances
    if (date.getFullYear() === 2025) {
      date.setFullYear(new Date().getFullYear());
    }

    // Format based on pattern
    switch (pattern) {
      case "MMM d":
        return fnsFormat(date, "MMM d");
      case "yyyy-MM-dd":
        return fnsFormat(date, "yyyy-MM-dd");
      case "dd/MM/yyyy":
        return fnsFormat(date, "dd/MM/yyyy");
      case "h:mm a":
        return fnsFormat(date, "h:mm a");
      case "full":
        return date.toLocaleString();
      case "local":
        return date.toLocaleString();
      case "relative":
        return getRelativeTimeString(date);
      default:
        return fnsFormat(date, pattern);
    }
  } catch (error) {
    console.error(`Error formatting date: ${dateValue}`, error);
    return "";
  }
};

/**
 * Get a relative time string (e.g., "2 days ago", "Just now")
 * @param {Date} date - The date to get the relative time for
 * @returns {string} - Relative time string
 */
export const getRelativeTimeString = (date) => {
  if (!date || !isValid(date)) return "";

  try {
    const now = new Date();

    // Fix 2025 year issue
    if (date.getFullYear() === 2025) {
      date.setFullYear(now.getFullYear());

      // If still in the future, subtract a year
      if (date > now) {
        date.setFullYear(now.getFullYear() - 1);
      }
    }

    // Calculate time difference
    const diffMs = now - date;
    const seconds = Math.floor(diffMs / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);

    if (days > 30) {
      return format(date, "MMM d, yyyy");
    } else if (days > 0) {
      return `${days} day${days > 1 ? "s" : ""} ago`;
    } else if (hours > 0) {
      return `${hours} hour${hours > 1 ? "s" : ""} ago`;
    } else if (minutes > 0) {
      return `${minutes} minute${minutes > 1 ? "s" : ""} ago`;
    } else {
      return "Just now";
    }
  } catch (error) {
    console.error("Error getting relative time:", error);
    return "";
  }
};
